import { SiteConfig } from '@utils';
import {
  FOOTER_FRAGMENT_URL,
  HEADER_FOOTER_BASE_URL,
  HEADER_FRAGMENT_URL,
  SITE_CONFIG_API_URL,
} from '@config/constants';

const FRAGMENT_PROXY = '/api/header-footer-fragment';

const getFragmentCdnOrigin = (): string | undefined => {
  if (!HEADER_FRAGMENT_URL) {
    return undefined;
  }

  try {
    return new URL(HEADER_FRAGMENT_URL).origin;
  } catch {
    return undefined;
  }
};

type SiteHeaderFooterData = {
  source?: string;
  headerUrl?: string;
  footerUrl?: string;
  headerFooterCSS?: string;
};

export type HeaderFooterConfig = {
  headerUrl: string;
  footerUrl: string;
  headerFooterCSS?: string;
  source?: string;
};

type CaptchaConfig = {
  fullMatchedDomain: string;
  headerFooterUrl: string;
};

type CaptchaSeparateReturn = {
  source: string;
  headerUrl: string;
  footerUrl: string;
  headerFooterCSS?: string;
};

const localToDevHostnameMap: Record<string, string> = {
  'local.checkout.wiley.com': 'dev.checkout.wiley.com',
  'local.graphicstandards.com': 'dev.graphicstandards.com',
  'local.vetdev.wiley.com': 'vetdev.wiley.com',
  'local.spectrabase.com': 'dev.spectrabase.com',
};

const getSiteConfigApiUrl = (): string => {
  let { hostname } = window.location;

  if (Object.keys(localToDevHostnameMap).includes(hostname)) {
    hostname = localToDevHostnameMap[hostname];
  }

  const encodedDomain = btoa(hostname);

  if (process.env.NODE_ENV === 'development') {
    return `/api/site-config?domain=${encodedDomain}`;
  }

  return `${SITE_CONFIG_API_URL}?domain=${encodedDomain}`;
};

const getFallbackHeaderFooterConfig = (): HeaderFooterConfig => ({
  headerUrl: HEADER_FRAGMENT_URL,
  footerUrl: FOOTER_FRAGMENT_URL,
});

export const resolveProxiedFetchUrl = (url: string): string => {
  if (process.env.NODE_ENV !== 'development') {
    return url;
  }

  const fragmentCdnOrigin = getFragmentCdnOrigin();

  if (fragmentCdnOrigin && url.startsWith(fragmentCdnOrigin)) {
    return url.replace(fragmentCdnOrigin, FRAGMENT_PROXY);
  }

  return url;
};

const resolveAEMResourceUrl = (
  resourceUrl: string,
  fragmentUrl: string,
): string => {
  try {
    return new URL(resourceUrl, fragmentUrl).toString();
  } catch {
    return resourceUrl;
  }
};

const isStylesheetAlreadyLoaded = (url: string): boolean => (
  Array.from(
    document.querySelectorAll<HTMLLinkElement>(
      'link[rel="stylesheet"]',
    ),
  ).some((link) => link.href === url && Boolean(link.sheet))
);

export const loadExternalStylesheet = (
  url: string,
): Promise<void> => new Promise((resolve, reject) => {
  if (!url) {
    resolve();
    return;
  }

  if (isStylesheetAlreadyLoaded(url)) {
    resolve();
    return;
  }

  const existingStylesheet = Array.from(
    document.querySelectorAll<HTMLLinkElement>(
      'link[rel="stylesheet"]',
    ),
  ).find((link) => link.href === url);

  if (existingStylesheet) {
    existingStylesheet.addEventListener(
      'load',
      () => resolve(),
      { once: true },
    );

    existingStylesheet.addEventListener(
      'error',
      () => reject(
        new Error(`Failed to load stylesheet: ${url}`),
      ),
      { once: true },
    );

    return;
  }

  const stylesheet = document.createElement('link');

  stylesheet.rel = 'stylesheet';
  stylesheet.href = url;

  stylesheet.onload = () => resolve();
  stylesheet.onerror = () => {
    reject(new Error(`Failed to load stylesheet: ${url}`));
  };

  document.head.appendChild(stylesheet);
});

export const getStylesheetUrls = (
  documentNode: Document,
  fragmentUrl: string,
): string[] => (
  Array.from(
    documentNode.querySelectorAll<HTMLLinkElement>(
      'link[rel="stylesheet"][href]',
    ),
  )
    .map((link) => link.getAttribute('href'))
    .filter((href): href is string => Boolean(href))
    .map((href) => resolveAEMResourceUrl(href, fragmentUrl))
);

export const getInlineStyleContents = (
  documentNode: Document,
): string[] => (
  Array.from(documentNode.querySelectorAll('style'))
    .map((style) => style.textContent?.trim() ?? '')
    .filter(Boolean)
);

const INLINE_STYLE_MARKER = 'data-wly-aem-inline-style';

export const injectInlineStyles = (
  styleContents: string[],
): void => {
  Array.from(new Set(styleContents.filter(Boolean))).forEach((css) => {
    const style = document.createElement('style');

    style.setAttribute(INLINE_STYLE_MARKER, 'true');
    style.textContent = css;

    document.head.appendChild(style);
  });
};

export const loadStylesheets = async (
  stylesheetUrls: string[],
): Promise<void> => {
  const uniqueStylesheetUrls = Array.from(
    new Set(stylesheetUrls.filter(Boolean)),
  );

  await Promise.allSettled(
    uniqueStylesheetUrls.map((url) => loadExternalStylesheet(url)),
  );

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }
};

export const getLocalizedHeaderURL = (
  headerUrl: string,
  country: string,
  lang: string,
): string => (
  headerUrl.replace(
    /\/[a-z]{2}\/[a-z]{2}\//,
    `/${country?.toLocaleLowerCase?.()}/${lang?.toLocaleLowerCase?.()}/`,
  )
);

export const getHeaderFooterConfig = async (): Promise<
HeaderFooterConfig
> => {
  const fallbackConfig = getFallbackHeaderFooterConfig();

  try {
    const { siteId } = SiteConfig.getConfig();

    if (!siteId) {
      // eslint-disable-next-line no-console
      console.warn(
        'siteId is unavailable. Using fallback header/footer URLs.',
      );

      return fallbackConfig;
    }

    const response = await fetch(getSiteConfigApiUrl());

    if (!response.ok) {
      throw new Error(
        `Site-config request failed with status ${response.status}`,
      );
    }

    const config = await response.json();

    if (config?.error) {
      const errorMessage = typeof config.error === 'string'
        ? config.error
        : 'Site-config returned an error';

      throw new Error(errorMessage);
    }

    const siteData = config?.[siteId] as SiteHeaderFooterData | undefined;

    if (!siteData) {
      // eslint-disable-next-line no-console
      console.warn(
        `Site-config is unavailable for siteId "${siteId}". `
        + 'Using fallback header/footer URLs.',
      );

      return fallbackConfig;
    }

    return {
      headerUrl: siteData.headerUrl || fallbackConfig.headerUrl,
      footerUrl: siteData.footerUrl || fallbackConfig.footerUrl,
      headerFooterCSS: siteData.headerFooterCSS,
      source: siteData.source,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      'Unable to load site-config. Using fallback header/footer URLs.',
      error,
    );

    return fallbackConfig;
  }
};

export const getCaptchaSiteKey = async (): Promise<
CaptchaConfig | undefined
> => {
  const { siteId } = SiteConfig.getConfig();
  const { hostname } = window.location;

  try {
    const response = await fetch(getSiteConfigApiUrl());

    if (!response.ok) {
      return undefined;
    }

    const config = await response.json();

    if (config?.error) {
      return undefined;
    }

    const siteData = config?.[siteId] as SiteHeaderFooterData | undefined;

    if (!siteData) {
      return undefined;
    }

    return {
      fullMatchedDomain: `https://${hostname}`,
      headerFooterUrl: siteData.headerUrl || HEADER_FOOTER_BASE_URL,
    };
  } catch {
    return undefined;
  }
};

export const buildHeaderFooterUrl = async (): Promise<
string | CaptchaSeparateReturn
> => {
  const { lang, iso } = SiteConfig.getConfig();
  const captchaConfig = await getCaptchaSiteKey();

  if (!captchaConfig) {
    throw new Error('Header Footer URL is undefined');
  }

  const { headerFooterUrl } = captchaConfig;

  if (!headerFooterUrl) {
    throw new Error('Header Footer URL is undefined');
  }

  if (
    headerFooterUrl.includes('{country}')
    && headerFooterUrl.includes('{lang}')
  ) {
    return headerFooterUrl
      .replace('{country}', iso)
      .replace('{lang}', lang);
  }

  return getLocalizedHeaderURL(headerFooterUrl, iso, lang);
};

export const triggerAEMHeaderCartIcon = () => {
  const { siteId, locale } = SiteConfig.getConfig();

  document.dispatchEvent(
    new CustomEvent('wly-cart:updated', {
      detail: {
        locale,
        site: siteId,
      },
    }),
  );
};

/** N4 fragment URLs — same as forgot-password-ui properties.ts */
export const headerFragmentUrl = HEADER_FRAGMENT_URL;
export const footerFragmentUrl = FOOTER_FRAGMENT_URL;
